import { Card } from "./shared";

const h1 = { fontFamily: "'Cormorant Garamond', serif", fontWeight: 800, fontSize: 28, color: "#163C2F", margin: 0 };
const h2 = { fontFamily: "'Cormorant Garamond', serif", fontWeight: 800, fontSize: 18, color: "#163C2F", marginBottom: 10 };
const body = { fontSize: 13, color: "#2B2B2B", lineHeight: 1.7, fontFamily: "'Manrope', sans-serif" };
const muted = { fontSize: 12, color: "#9B9690", lineHeight: 1.7, fontFamily: "'Manrope', sans-serif" };

function Section({ title, children }) {
  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={h2}>{title}</div>
      {children}
    </Card>
  );
}

export default function HelpFees({ setPage }) {
  return (
    <div style={{ maxWidth: 680, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <button
          onClick={() => setPage("Settings")}
          style={{ background: "none", border: "none", color: "#9B9690", fontSize: 13, cursor: "pointer", fontFamily: "'Manrope', sans-serif", padding: 0, marginBottom: 14, display: "flex", alignItems: "center", gap: 5 }}
        >
          ← Back to Settings
        </button>
        <h1 style={h1}>Understanding Leasly &amp; Stripe Fees</h1>
        <div style={{ ...muted, marginTop: 6 }}>Everything you need to know about payment processing costs for online rent collection.</div>
      </div>

      {/* What is Stripe? */}
      <Section title="What is Stripe?">
        <p style={body}>
          Stripe is a secure, industry-standard payment processor used by millions of businesses worldwide. Leasly uses Stripe to handle all online rent payments so your tenants' bank and card information is never stored on Leasly's servers.
        </p>
      </Section>

      {/* Payment method fees */}
      <Section title="Payment Method Fees (charged by Stripe)">
        <p style={{ ...muted, marginBottom: 14 }}>These fees are collected by Stripe and deducted from the rent amount before the funds reach your account.</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {/* ACH */}
          <div style={{ background: "#EAF2ED", border: "1.5px solid #163C2F33", borderRadius: 10, padding: "14px 16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: "#163C2F", fontFamily: "'Manrope', sans-serif" }}>ACH / Bank Transfer</span>
              <span style={{ fontWeight: 700, fontSize: 14, color: "#163C2F", fontFamily: "'Manrope', sans-serif" }}>0.8% · max $5.00</span>
            </div>
            <div style={muted}>Settles in 3–5 business days. <strong style={{ color: "#163C2F" }}>Recommended for most landlords</strong> — lowest cost, especially on higher rent amounts.</div>
          </div>
          {/* Card */}
          <div style={{ background: "#F5F1EA", border: "1px solid #E8E4DC", borderRadius: 10, padding: "14px 16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: "#2B2B2B", fontFamily: "'Manrope', sans-serif" }}>Credit / Debit Card</span>
              <span style={{ fontWeight: 700, fontSize: 14, color: "#2B2B2B", fontFamily: "'Manrope', sans-serif" }}>2.9% + $0.30</span>
            </div>
            <div style={muted}>Settles in 2 business days. Higher cost — most practical when a tenant cannot pay via ACH.</div>
          </div>
        </div>
      </Section>

      {/* Real dollar examples */}
      <Section title="Real Dollar Examples">
        <p style={{ ...muted, marginBottom: 14 }}>These examples show Stripe's fee only, before the Leasly platform fee.</p>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "'Manrope', sans-serif", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "1.5px solid #E8E4DC" }}>
              {["Rent Amount", "Method", "Stripe Fee", "You Receive"].map(h => (
                <th key={h} style={{ padding: "8px 10px", textAlign: "left", fontSize: 11, color: "#9B9690", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              ["$1,000", "ACH",  "$8.00",  "~$992.00"],
              ["$1,000", "Card", "$29.30", "~$970.70"],
              ["$1,200", "ACH",  "$5.00 (capped)", "~$1,195.00"],
            ].map(([rent, method, fee, recv], i) => (
              <tr key={i} style={{ borderBottom: "1px solid #F0EDE6", background: i % 2 === 0 ? "#fff" : "#FAFAF8" }}>
                <td style={{ padding: "10px 10px", fontWeight: 600, color: "#163C2F" }}>{rent}</td>
                <td style={{ padding: "10px 10px", color: "#2B2B2B" }}>{method}</td>
                <td style={{ padding: "10px 10px", color: "#ef4444", fontWeight: 600 }}>{fee}</td>
                <td style={{ padding: "10px 10px", color: "#22c55e", fontWeight: 700 }}>{recv}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* Leasly $2.95 fee */}
      <Section title="The Leasly $2.95 Platform Fee">
        <p style={body}>
          Leasly charges a <strong>$2.95 fee per online payment</strong> to cover platform costs. In your Payment Settings you can choose to absorb this fee yourself or pass it to your tenant at checkout. Either way, Stripe's processing fee still applies separately.
        </p>
      </Section>

      {/* Fee toggle explained */}
      <Section title="Fee Toggle Explained">
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ background: "#F5F1EA", borderRadius: 10, padding: "14px 16px", border: "1px solid #E8E4DC" }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#163C2F", fontFamily: "'Manrope', sans-serif", marginBottom: 4 }}>Landlord absorbs fee</div>
            <div style={muted}>Tenant pays rent only. $2.95 is deducted from your payout along with Stripe's processing fee.</div>
          </div>
          <div style={{ background: "#F5F1EA", borderRadius: 10, padding: "14px 16px", border: "1px solid #E8E4DC" }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#163C2F", fontFamily: "'Manrope', sans-serif", marginBottom: 4 }}>Pass fee to tenant</div>
            <div style={muted}>Tenant pays rent + $2.95 at checkout. Stripe's processing fee is still deducted from your payout.</div>
          </div>
        </div>
        <div style={{ marginTop: 14, background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 8, padding: "10px 14px" }}>
          <span style={{ fontSize: 12, color: "#92400E", fontFamily: "'Manrope', sans-serif", lineHeight: 1.6 }}>
            <strong>Note:</strong> No matter which option you choose, Stripe's payment processing fee is always deducted from your payout. The $2.95 Leasly fee only controls whether Leasly's cut comes from you or from the tenant.
          </span>
        </div>
      </Section>

      {/* Support footer */}
      <div style={{ textAlign: "center", padding: "20px 0 32px", borderTop: "1px solid #E8E4DC", marginTop: 8 }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 16, color: "#163C2F", marginBottom: 6 }}>Still have questions?</div>
        <a href="mailto:support@leaslypro.com" style={{ fontSize: 13, color: "#214D3A", fontFamily: "'Manrope', sans-serif", fontWeight: 600, textDecoration: "underline" }}>
          support@leaslypro.com
        </a>
      </div>
    </div>
  );
}
